import React, { useState, useEffect } from 'react';
import { X, Plus, DollarSign, Calendar, RefreshCw, AlertCircle } from 'lucide-react';

const DaycareAdditionalChargesModal = ({ 
  isOpen, 
  onClose, 
  enrollment, 
  apiCall, 
  showSuccess, 
  showError 
}) => {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    chargeType: 'Registration',
    description: '',
    amount: '',
    discountAmount: 0,        // ✅ BONUS: Discount
    isRecurring: false,        // ✅ BONUS: Recurring
    recurrenceFrequency: '',   // ✅ BONUS: Frequency
    dueDate: ''                // ✅ BONUS: Due date
  });

  const chargeTypes = [
    'Registration',
    'Books',
    'Uniform',
    'Materials',
    'Field Trip',
    'Equipment',
    'Insurance',
    'Other'
  ];

  const recurrenceFrequencies = ['Monthly', 'Quarterly', 'Yearly'];

  useEffect(() => {
    if (isOpen && enrollment) {
      loadCharges();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, enrollment]);

  const loadCharges = async () => {
    try {
      setLoading(true);
      const data = await apiCall(
        `/daycare/additional-charges?enrollmentId=${enrollment.EnrollmentID}`
      );
      setCharges(data);
    } catch (error) {
      showError('Error loading charges: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    const discount = parseFloat(formData.discountAmount) || 0;
    const amount = parseFloat(formData.amount);
    
    if (discount > amount) {
      showError('Discount cannot be greater than amount');
      return;
    }

    if (formData.isRecurring && !formData.recurrenceFrequency) {
      showError('Please select recurrence frequency');
      return;
    }

    try {
      await apiCall('/daycare/additional-charges', {
        method: 'POST',
        body: JSON.stringify({
          enrollmentId: enrollment.EnrollmentID,
          familyId: enrollment.FamilyID,
          childId: enrollment.ChildID,
          academicYear: enrollment.AcademicYear,
          chargeType: formData.chargeType,
          description: formData.description,
          amount: amount,
          discountAmount: discount,
          isRecurring: formData.isRecurring,
          recurrenceFrequency: formData.isRecurring ? formData.recurrenceFrequency : null,
          dueDate: formData.dueDate || null
        })
      });

      showSuccess('Additional charge added successfully');
      setFormData({ 
        chargeType: 'Registration', 
        description: '', 
        amount: '', 
        discountAmount: 0,
        isRecurring: false,
        recurrenceFrequency: '',
        dueDate: ''
      });
      setShowAddForm(false);
      loadCharges();
    } catch (error) {
      showError('Error adding charge: ' + error.message);
    }
  };

  const handleCancel = async (chargeId) => {
    const reason = window.prompt('Reason for cancellation (optional):');
    if (reason === null) return; // User clicked cancel

    try {
      await apiCall(`/daycare/additional-charges/${chargeId}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason })
      });
      showSuccess('Charge cancelled');
      loadCharges();
    } catch (error) {
      showError('Error cancelling charge: ' + error.message);
    }
  };

  const getStatusBadge = (paymentStatus, chargeStatus) => {
    if (chargeStatus === 'Cancelled') {
      return <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">Cancelled</span>;
    }
    
    const styles = {
      Paid: 'bg-green-100 text-green-800',
      Partial: 'bg-yellow-100 text-yellow-800',
      Billed: 'bg-blue-100 text-blue-800',
      Pending: 'bg-orange-100 text-orange-800'
    };
    return <span className={`px-2 py-1 text-xs font-medium rounded ${styles[paymentStatus] || ''}`}>{paymentStatus}</span>;
  };

  const calculateFinalAmount = () => {
    const amount = parseFloat(formData.amount) || 0;
    const discount = parseFloat(formData.discountAmount) || 0;
    return Math.max(0, amount - discount);
  };

  const summary = charges.reduce((acc, charge) => {
    acc.total += charge.Amount;
    acc.discount += charge.DiscountAmount || 0;
    acc.final += charge.FinalAmount;
    if (charge.PaymentStatus === 'Paid') acc.paid += charge.FinalAmount;
    if (charge.PaymentStatus !== 'Paid' && charge.ChargeStatus !== 'Cancelled') {
      acc.pending += charge.RemainingAmount || 0;
    }
    return acc;
  }, { total: 0, discount: 0, final: 0, paid: 0, pending: 0 });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Additional Charges</h2>
            <p className="text-sm text-blue-100">
              {enrollment?.FirstName} {enrollment?.LastName} - {enrollment?.AcademicYear}
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Summary Cards */}
          <div className="grid grid-cols-5 gap-3 mb-6">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Total</p>
              <p className="text-lg font-bold text-gray-900">${summary.total.toFixed(2)}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Discounts</p>
              <p className="text-lg font-bold text-orange-600">-${summary.discount.toFixed(2)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Final Amount</p>
              <p className="text-lg font-bold text-blue-600">${summary.final.toFixed(2)}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Paid</p>
              <p className="text-lg font-bold text-green-600">${summary.paid.toFixed(2)}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Pending</p>
              <p className="text-lg font-bold text-yellow-600">${summary.pending.toFixed(2)}</p>
            </div>
          </div>

          {/* Add Charge Button */}
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="mb-4 flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Charge
            </button>
          )}

          {/* Add Charge Form */}
          {showAddForm && (
            <form onSubmit={handleSubmit} className="mb-6 bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Charge Type *
                  </label>
                  <select
                    value={formData.chargeType}
                    onChange={(e) => setFormData({ ...formData, chargeType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    {chargeTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.discountAmount}
                    onChange={(e) => setFormData({ ...formData, discountAmount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Optional description"
                />
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      checked={formData.isRecurring}
                      onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                      className="mr-2"
                    />
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Recurring Charge
                  </label>
                  {formData.isRecurring && (
                    <select
                      value={formData.recurrenceFrequency}
                      onChange={(e) => setFormData({ ...formData, recurrenceFrequency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required={formData.isRecurring}
                    >
                      <option value="">Select frequency...</option>
                      {recurrenceFrequencies.map(freq => (
                        <option key={freq} value={freq}>{freq}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Final Amount Display */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Final Amount:</span>
                  <span className="text-xl font-bold text-blue-600">${calculateFinalAmount().toFixed(2)}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Charge
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setFormData({ 
                      chargeType: 'Registration', 
                      description: '', 
                      amount: '',
                      discountAmount: 0,
                      isRecurring: false,
                      recurrenceFrequency: '',
                      dueDate: ''
                    });
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Charges List */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading charges...</div>
          ) : charges.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <DollarSign className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No additional charges yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {charges.map((charge) => {
                const isOverdue = charge.DueDate && new Date(charge.DueDate) < new Date() && 
                                  charge.PaymentStatus !== 'Paid';
                
                return (
                  <div key={charge.ChargeID} className={`bg-white border rounded-lg p-4 ${
                    isOverdue ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{charge.ChargeType}</h3>
                          {getStatusBadge(charge.PaymentStatus, charge.ChargeStatus)}
                          {charge.IsRecurring && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              {charge.RecurrenceFrequency}
                            </span>
                          )}
                          {isOverdue && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Overdue
                            </span>
                          )}
                        </div>
                        {charge.Description && (
                          <p className="text-sm text-gray-600 mb-2">{charge.Description}</p>
                        )}
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>Charged: {new Date(charge.ChargeDate).toLocaleDateString()}</span>
                          {charge.DueDate && (
                            <span className="flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              Due: {new Date(charge.DueDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="mb-1">
                          {charge.DiscountAmount > 0 && (
                            <div className="text-sm text-gray-500 line-through">${charge.Amount.toFixed(2)}</div>
                          )}
                          <p className="text-xl font-bold text-gray-900">${charge.FinalAmount.toFixed(2)}</p>
                          {charge.DiscountAmount > 0 && (
                            <div className="text-xs text-orange-600">
                              Discount: -${charge.DiscountAmount.toFixed(2)}
                            </div>
                          )}
                        </div>
                        {charge.PaymentStatus === 'Paid' && (
                          <div className="text-xs text-green-600 font-medium">✓ Paid</div>
                        )}
                        {charge.RemainingAmount > 0 && charge.ChargeStatus !== 'Cancelled' && (
                          <div className="text-xs text-yellow-600">
                            Remaining: ${charge.RemainingAmount.toFixed(2)}
                          </div>
                        )}
                        {charge.PaymentStatus !== 'Paid' && charge.ChargeStatus !== 'Cancelled' && (
                          <button
                            onClick={() => handleCancel(charge.ChargeID)}
                            className="mt-2 text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DaycareAdditionalChargesModal;
